import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Grid,
  Typography,
  Slider,
  Switch,
  Tooltip,
} from "@mui/material";
import BlockIcon from "@mui/icons-material/Block";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";

import ColorSwatch from "../Swatch";
import CustomColorPicker from "../CustomColorPicker";
import { colors, gradients } from "../../constants/background";
import { PRESET_LOOKS, WALLPAPER_TABS, T } from "./constants";
import {
  DEFAULT_CARD_RADIUS,
  MIN_CARD_RADIUS,
  MAX_CARD_RADIUS,
  GALLERY_PRESETS,
  DEFAULT_PRESET_ID,
  applyPresetToTheme,
  resolveActivePresetId,
  isImageWallpaperRef,
  resolveWallpaperTab,
} from "./galleryTheme";
import GalleryPaletteLibrary from "./GalleryPaletteLibrary";
import PresetCarousel from "./PresetCarousel";
import { useImagePalette } from "./useImagePalette";
// ISP-9999 (gordon-0913-gallery-theme-wallpaper-pattern-image-parity): the
// Pattern/Image tabs below keep this panel COMPACT (Gordon UX note 2026-09-13:
// "robust sections on the Edit Wheel page... poor UX/displacement... if not
// handled carefully") — a small strip here, the full picker in a popup. Every
// popup mounts the SAME shared component the Edit-Wheel builder's Wallpaper
// picker uses (BackgroundPatternPanel / UploadMedia / MediaLibraryReuse /
// RecentMedia / EmbedURL — see PatternPickerDialog.jsx / ImagePickerDialog.jsx),
// never a re-implementation. See galleryTheme.js's "PATTERN TAB SOURCE OF
// TRUTH" note for why the dead legacy 28-pattern <Pattern/> mask component was
// NOT the reuse target, and the unit's READY notes for why SegmentColor itself
// was not reused for Accent/Background (builder-only redux wiring —
// selectedSegment/colorPalette reducers, AI suggestion calls — that has no
// meaning outside the builder); this panel's own ColorField (below, already
// used for Accent/Background) is the presentational swatch-row SegmentColor is
// built on top of.
import PatternPickerDialog from "./PatternPickerDialog";
import ImagePickerDialog from "./ImagePickerDialog";
import { buildPatternCss, parsePatternCss } from "../backgroundPatterns/toBackgroundCss";
import { PATTERNS, SCALE_DEFAULT, DEFAULT_FG, DEFAULT_BG } from "../backgroundPatterns/patternGenerators";

// Compact strip "quick picks": the first QUICK_PICK_COUNT patterns from the
// library. There is no MRU/usage-tracking store for patterns today (measured
// in STEP 1 — see READY), so "most recent/used" is approximated as "first N of
// the library" rather than inventing new persistence for this unit; flagged in
// the unit's design-debt-register note as a follow-up, not silently dropped.
const QUICK_PICK_COUNT = 6;
const PATTERN_STRIP_SCALE = 24;

const SectionTitle = ({ children, hint, id }) => (
  <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", mb: "8px" }}>
    <Typography id={id} sx={{ fontWeight: 700, fontSize: 14, fontFamily: T.fontBody, color: T.sectionTitle }}>
      {children}
    </Typography>
    {hint && (
      <Typography sx={{ fontSize: 12, color: T.muted, fontFamily: T.fontBody }}>{hint}</Typography>
    )}
  </Box>
);

// A reusable preset/free-form color field (mirrors VaultDesign.ColorField).
// `imageSwatches`, when non-empty, renders a labelled "From your image" row
// above the default grid -- same swatch component/size/selected-ring/hex
// tooltip as the grid below, clicking one sets the color exactly like a grid
// swatch (see useImagePalette for when this is populated/cleared).
const ColorField = ({ value, onChange, ariaLabel, imageSwatches = [] }) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [draft, setDraft] = useState(value || "#FFFFFF");
  return (
    <Box>
      {imageSwatches.length > 0 && (
        <Box sx={{ mb: "10px" }} data-testid={`gt-image-palette-row-${ariaLabel.replace(/\s+/g, "-").toLowerCase()}`}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.muted, fontFamily: T.fontBody, mb: "6px" }}>
            From your image
          </Typography>
          <Grid container spacing={1} alignItems="center">
            {imageSwatches.map((c) => (
              <Grid item key={`img-${c}`}>
                <Box
                  role="button"
                  tabIndex={0}
                  aria-label={`${ariaLabel} from your image ${c}`}
                  aria-pressed={value === c}
                  onClick={() => onChange(c)}
                  onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onChange(c))}
                  sx={{ borderRadius: "50%", "&:focus-visible": { outline: `2px solid ${T.primary}`, outlineOffset: 2 } }}
                >
                  <ColorSwatch color={c} borderColor={value === c ? "#BBBBBB" : undefined} />
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
      <Grid container spacing={1} alignItems="center">
        {colors.map((c) => (
          <Grid item key={c}>
            <Box
              role="button"
              tabIndex={0}
              aria-label={`${ariaLabel} ${c}`}
              aria-pressed={value === c}
              onClick={() => onChange(c)}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onChange(c))}
              sx={{ borderRadius: "50%", "&:focus-visible": { outline: `2px solid ${T.primary}`, outlineOffset: 2 } }}
            >
              <ColorSwatch color={c} borderColor={value === c ? "#BBBBBB" : undefined} />
            </Box>
          </Grid>
        ))}
        <Grid item>
          <Tooltip title="Custom color" arrow>
            <Box
              role="button"
              tabIndex={0}
              aria-label={`${ariaLabel} custom color`}
              onClick={() => {
                setDraft(value || "#FFFFFF");
                setPickerOpen(true);
              }}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), setPickerOpen(true))}
              sx={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                border: "1px dashed #BBBBBB",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                fontSize: 18,
                color: T.primary,
                "&:focus-visible": { outline: `2px solid ${T.primary}`, outlineOffset: 2 },
              }}
            >
              +
            </Box>
          </Tooltip>
        </Grid>
      </Grid>
      <CustomColorPicker
        visible={pickerOpen}
        color={draft}
        onColorChange={setDraft}
        onClose={() => setPickerOpen(false)}
        onConfirm={(c) => {
          onChange(c);
          setPickerOpen(false);
        }}
      />
    </Box>
  );
};

const NoneTile = ({ active, onClick, label }) => (
  <Tooltip title={label} arrow>
    <Box
      role="button"
      tabIndex={0}
      aria-label={label}
      aria-pressed={active}
      onClick={onClick}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onClick())}
      sx={{
        width: 32,
        height: 32,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        borderRadius: "50%",
        border: active ? `1px solid ${T.primary}` : "1px solid transparent",
        "&:focus-visible": { outline: `2px solid ${T.primary}`, outlineOffset: 2 },
      }}
    >
      <BlockIcon sx={{ color: "#BBBBBB" }} />
    </Box>
  </Tooltip>
);

// BUG FIX (ISP-9999, "Wallpaper... need the same option set + on-brand
// circular swatches as the Edit-Wheel page"): this used to be a hand-rolled
// rounded-SQUARE tile (borderRadius:"8px"), the only swatch shape in this
// panel that didn't match the Edit-Wheel Wallpaper picker's circular
// ColorSwatch (src/components/Swatch) -- the Accent/Background fields in this
// same panel already reuse ColorSwatch and were already circular. Reusing the
// same shared component makes every swatch on-brand and consistent.
const SwatchTile = ({ bg, active, onClick, label }) => (
  <Tooltip title={label} arrow>
    <Box
      role="button"
      tabIndex={0}
      aria-label={label}
      aria-pressed={active}
      onClick={onClick}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onClick())}
      sx={{
        borderRadius: "50%",
        cursor: "pointer",
        "&:focus-visible": { outline: `2px solid ${T.primary}`, outlineOffset: 2 },
      }}
    >
      <ColorSwatch color={bg} borderColor={active ? T.primary : undefined} />
    </Box>
  </Tooltip>
);

const GalleryThemePanel = ({ theme, setField, setTheme, vaultTheme }) => {
  // ISP-9999 (gordon-0923-P2-gallery-theme-reopen-on-saved-wallpaper-type-tab):
  // reopen on the tab that matches the SAVED wallpaper (image/pattern/
  // gradient/color), not a hardcoded default — lazy init so it's read once
  // per mount (the popup unmounts on close, so this re-derives every reopen).
  const [wallpaperTab, setWallpaperTab] = useState(() => resolveWallpaperTab(theme.wallpaper));
  // batch-proof-fe-69e15651 item 6069: on the full-page /gallery/theme route
  // (GalleryThemeStudio), this panel first mounts on the SAME render
  // `isLoading` flips false, using whatever `draft` the parent's `useState`
  // still holds from ITS OWN mount (profile not fetched yet) — the effect
  // that reconciles `draft` with the real fetched `serverBaseline` commits
  // AFTER that render. So the lazy initializer above can capture a
  // still-empty `theme.wallpaper` and this component never re-derives once
  // the real value lands a beat later (measured live: an account with a
  // persisted `wallpaper.type:"image"` still reopened on the Color tab).
  // Keep following `theme.wallpaper` until the user manually picks a tab,
  // then stop — so a background refetch (e.g. right after Save) can never
  // yank the user off a tab they're actively editing.
  const userPickedWallpaperTabRef = useRef(false);
  useEffect(() => {
    if (userPickedWallpaperTabRef.current) return;
    setWallpaperTab(resolveWallpaperTab(theme.wallpaper));
  }, [theme.wallpaper]);
  const selectWallpaperTab = (tabId) => {
    userPickedWallpaperTabRef.current = true;
    setWallpaperTab(tabId);
  };
  const [libOpen, setLibOpen] = useState(false);
  const [patternDialogOpen, setPatternDialogOpen] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);

  // Current pattern selection, decoded back from the persisted CSS string (or
  // null when the wallpaper isn't a pattern) — feeds the compact strip's
  // current-selection thumbnail + seeds new quick-pick clicks with the same
  // scale/colours the user already chose in the full picker.
  const currentPattern =
    typeof theme.wallpaper === "string" ? parsePatternCss(theme.wallpaper) : null;

  // "From your image" swatch row source (ISP-9999,
  // gordon-0923-gallery-theme-image-wallpaper-palette-row): [] for every
  // non-image wallpaper, hiding the row; recomputes when the image changes.
  const { palette: imagePalette } = useImagePalette(theme.wallpaper);

  // Selecting any manual control diverges from a one-tap preset — deselect it.
  const patch = (obj) => setTheme({ ...theme, ...obj, preset: undefined });

  const applyPreset = (id) => {
    if (id !== DEFAULT_PRESET_ID && !GALLERY_PRESETS[id]) return;
    setTheme(applyPresetToTheme(theme, id));
  };

  const onPaletteSelect = (palette) => {
    // apply the palette's first color as accent, last as background surface.
    const [first] = palette.colors;
    const last = palette.colors[palette.colors.length - 1];
    patch({ accent: first, bg_color: last });
  };

  const generatePalette = () => {
    // deterministic-ish "generate": rotate through the app palette from a random
    // seed so it feels fresh but always yields safe values.
    const i = Math.floor(Math.random() * colors.length);
    const accent = colors[i];
    const bg = colors[(i + 5) % colors.length];
    patch({ accent, bg_color: bg });
  };

  return (
    // BUG FIX (ISP-9999, gallery-theme-modal-clips-390): minWidth:300 was
    // unconditional, so even after GalleryThemeStudio stacks panel-over-preview
    // below `sm`, this panel still refused to shrink under 300px — wider than
    // most phone viewports (e.g. 390px minus the modal's own padding/border).
    // Below `sm` let it fill the stacked column's actual width; the desktop
    // 300–380px band only applies once there's a preview pane beside it.
    <Box
      data-testid="gt-panel-root"
      sx={{ height: "100%", overflowY: "auto", p: "18px", width: { xs: "100%", sm: "auto" }, minWidth: { xs: 0, sm: 300 }, maxWidth: { xs: "100%", sm: 380 } }}
    >
      {/* header */}
      <Box sx={{ display: "flex", gap: "12px", alignItems: "center", mb: "18px" }}>
        <Box sx={{ width: 40, height: 40, borderRadius: "10px", background: "#F1E9FE", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <SettingsRoundedIcon sx={{ color: T.primary }} />
        </Box>
        <Box>
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: T.ink, fontFamily: T.fontBody }}>Gallery Theme</Typography>
          <Typography sx={{ fontSize: 13, color: T.muted, fontFamily: T.fontBody }}>Style your public Gallery — presets or full control</Typography>
        </Box>
      </Box>

      {/* preset looks */}
      <Box sx={{ mb: "22px" }}>
        <SectionTitle hint="one tap, done">Preset looks</SectionTitle>
        <PresetCarousel presets={PRESET_LOOKS} activeId={resolveActivePresetId(theme)} onSelect={applyPreset} />
      </Box>

      {/* palette library */}
      <Box sx={{ mb: "22px" }}>
        <SectionTitle hint="from your libraries">Palette Library</SectionTitle>
        <Box sx={{ display: "flex", gap: "10px" }}>
          <Box
            role="button"
            tabIndex={0}
            aria-label="Open Color Library"
            onClick={() => setLibOpen(true)}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), setLibOpen(true))}
            sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", background: T.surface, borderRadius: "8px", py: "9px", cursor: "pointer", fontSize: 13, fontWeight: 600, fontFamily: T.fontBody, color: T.ink, "&:focus-visible": { outline: `2px solid ${T.primary}` } }}
          >
            <MenuBookRoundedIcon sx={{ fontSize: 18 }} /> Library
          </Box>
          <Box
            role="button"
            tabIndex={0}
            aria-label="Generate palette"
            onClick={generatePalette}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), generatePalette())}
            sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", background: T.primary, borderRadius: "8px", py: "9px", cursor: "pointer", fontSize: 13, fontWeight: 600, fontFamily: T.fontBody, color: "#fff", "&:focus-visible": { outline: `2px solid ${T.ink}` } }}
          >
            <AutoAwesomeRoundedIcon sx={{ fontSize: 18 }} /> Generate
          </Box>
        </Box>
        <Typography sx={{ fontSize: 12, color: T.muted, fontFamily: T.fontBody, mt: "6px" }}>
          Opens the same Color Library used in the Wheel Builder.
        </Typography>
      </Box>

      {/* wallpaper */}
      <Box sx={{ mb: "22px" }}>
        <SectionTitle>Wallpaper</SectionTitle>
        <Box role="tablist" aria-label="Wallpaper type" sx={{ display: "flex", gap: "16px", mb: "12px", borderBottom: `1px solid ${T.line}` }}>
          {WALLPAPER_TABS.map((tb) => (
            <Box
              key={tb.id}
              role="tab"
              aria-selected={wallpaperTab === tb.id}
              tabIndex={0}
              onClick={() => selectWallpaperTab(tb.id)}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && selectWallpaperTab(tb.id)}
              sx={{
                pb: "6px",
                cursor: "pointer",
                fontSize: 13,
                fontWeight: 600,
                fontFamily: T.fontBody,
                color: wallpaperTab === tb.id ? T.primary : T.muted,
                borderBottom: wallpaperTab === tb.id ? `2px solid ${T.primary}` : "2px solid transparent",
                "&:focus-visible": { outline: `2px solid ${T.primary}`, outlineOffset: 2 },
              }}
            >
              {tb.label}
            </Box>
          ))}
        </Box>

        {wallpaperTab === "color" && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
            <NoneTile active={!theme.wallpaper} onClick={() => patch({ wallpaper: "" })} label="No wallpaper (neutral)" />
            {/* 13 = the #FFFFFF swatch (first, swatchPalette.js) + the 12 colours this strip showed before it. */}
            {colors.slice(0, 13).map((c) => (
              <SwatchTile key={c} bg={c} active={theme.wallpaper === c} onClick={() => patch({ wallpaper: c })} label={c} />
            ))}
          </Box>
        )}
        {wallpaperTab === "gradient" && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
            <NoneTile active={!theme.wallpaper} onClick={() => patch({ wallpaper: "" })} label="No wallpaper (neutral)" />
            {gradients.slice(0, 11).map((g) => (
              <SwatchTile key={g} bg={g} active={theme.wallpaper === g} onClick={() => patch({ wallpaper: g })} label="Gradient" />
            ))}
          </Box>
        )}
        {wallpaperTab === "pattern" && (
          <Box data-testid="gt-pattern-strip">
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px", mb: "10px" }}>
              <Box
                role="img"
                aria-label={currentPattern ? "Current pattern" : "No pattern selected"}
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "10px",
                  border: `2px solid ${T.primary}`,
                  background: currentPattern
                    ? buildPatternCss({
                        id: currentPattern.id,
                        scale: PATTERN_STRIP_SCALE,
                        fg: currentPattern.fg,
                        bg: currentPattern.bg,
                      })
                    : "#fff",
                }}
              />
              {PATTERNS.slice(0, QUICK_PICK_COUNT).map((p) => (
                <Tooltip title={p.label} arrow key={p.id}>
                  <Box
                    role="button"
                    tabIndex={0}
                    aria-label={`Pattern ${p.label}`}
                    aria-pressed={currentPattern?.id === p.id}
                    onClick={() =>
                      patch({
                        wallpaper: buildPatternCss({
                          id: p.id,
                          scale: currentPattern?.scale || SCALE_DEFAULT,
                          fg: currentPattern?.fg || DEFAULT_FG,
                          bg: currentPattern?.bg || DEFAULT_BG,
                        }),
                      })
                    }
                    onKeyDown={(e) =>
                      (e.key === "Enter" || e.key === " ") &&
                      (e.preventDefault(),
                      patch({
                        wallpaper: buildPatternCss({
                          id: p.id,
                          scale: currentPattern?.scale || SCALE_DEFAULT,
                          fg: currentPattern?.fg || DEFAULT_FG,
                          bg: currentPattern?.bg || DEFAULT_BG,
                        }),
                      }))
                    }
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "10px",
                      cursor: "pointer",
                      border: currentPattern?.id === p.id ? `2px solid ${T.primary}` : `1px solid ${T.line}`,
                      background: buildPatternCss({
                        id: p.id,
                        scale: PATTERN_STRIP_SCALE,
                        fg: currentPattern?.fg || DEFAULT_FG,
                        bg: currentPattern?.bg || DEFAULT_BG,
                      }),
                      "&:focus-visible": { outline: `2px solid ${T.primary}`, outlineOffset: 2 },
                    }}
                  />
                </Tooltip>
              ))}
            </Box>
            <Box
              role="button"
              tabIndex={0}
              aria-label="Browse all patterns"
              onClick={() => setPatternDialogOpen(true)}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), setPatternDialogOpen(true))}
              sx={{ display: "inline-flex", alignItems: "center", gap: "8px", background: T.surface, borderRadius: "8px", px: "12px", py: "8px", cursor: "pointer", fontSize: 13, fontWeight: 600, fontFamily: T.fontBody, color: T.ink, "&:focus-visible": { outline: `2px solid ${T.primary}` } }}
            >
              Browse all patterns
            </Box>
            <PatternPickerDialog
              open={patternDialogOpen}
              onClose={() => setPatternDialogOpen(false)}
              value={theme.wallpaper}
              onApply={(css) => patch({ wallpaper: css })}
              segmentColor={theme.accent}
            />
          </Box>
        )}
        {wallpaperTab === "image" && (
          <Box data-testid="gt-image-strip">
            <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mb: "10px" }}>
              {isImageWallpaperRef(theme.wallpaper) ? (
                <>
                  <Box
                    component="img"
                    src={theme.wallpaper.url}
                    alt="Current wallpaper image"
                    sx={{ width: 56, height: 56, borderRadius: "10px", objectFit: "cover", border: `1px solid ${T.line}` }}
                  />
                  <NoneTile active={false} onClick={() => patch({ wallpaper: "" })} label="Remove image" />
                </>
              ) : (
                <NoneTile active onClick={() => {}} label="No image selected" />
              )}
            </Box>
            <Box
              role="button"
              tabIndex={0}
              aria-label="Choose image"
              onClick={() => setImageDialogOpen(true)}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), setImageDialogOpen(true))}
              sx={{ display: "inline-flex", alignItems: "center", gap: "8px", background: T.surface, borderRadius: "8px", px: "12px", py: "8px", cursor: "pointer", fontSize: 13, fontWeight: 600, fontFamily: T.fontBody, color: T.ink, "&:focus-visible": { outline: `2px solid ${T.primary}` } }}
            >
              Choose image
            </Box>
            <ImagePickerDialog
              open={imageDialogOpen}
              onClose={() => setImageDialogOpen(false)}
              onSelect={(ref) => {
                patch({ wallpaper: ref });
                setImageDialogOpen(false);
              }}
            />
          </Box>
        )}
      </Box>

      {/* accent */}
      <Box sx={{ mb: "22px" }}>
        <SectionTitle>Accent colour</SectionTitle>
        <ColorField
          value={theme.accent}
          onChange={(v) => patch({ accent: v })}
          ariaLabel="Accent colour"
          imageSwatches={imagePalette}
        />
      </Box>

      {/* background */}
      <Box sx={{ mb: "22px" }}>
        <SectionTitle>Background colour</SectionTitle>
        <ColorField
          value={theme.bg_color}
          onChange={(v) => patch({ bg_color: v })}
          ariaLabel="Background colour"
          imageSwatches={imagePalette}
        />
      </Box>

      {/* card corners */}
      <Box sx={{ mb: "22px" }}>
        <SectionTitle id="gt-radius-label" hint={`${theme.card_radius ?? DEFAULT_CARD_RADIUS}px`}>
          Card corners
        </SectionTitle>
        <Slider
          value={theme.card_radius ?? DEFAULT_CARD_RADIUS}
          min={MIN_CARD_RADIUS}
          max={MAX_CARD_RADIUS}
          onChange={(_, v) => setField("card_radius", v)}
          aria-labelledby="gt-radius-label"
          sx={{ color: T.primary }}
        />
      </Box>

      {/* match link vault */}
      <Box sx={{ mb: "22px" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography sx={{ fontWeight: 700, fontSize: 14, fontFamily: T.fontBody, color: T.sectionTitle }}>
            Match Link Vault
          </Typography>
          <Switch
            checked={!!theme.match_link_vault}
            onChange={(e) => setField("match_link_vault", e.target.checked)}
            disabled={!vaultTheme || Object.keys(vaultTheme || {}).length === 0}
            inputProps={{ "aria-label": "Match Link Vault theme" }}
            sx={{ "& .Mui-checked": { color: T.primary }, "& .Mui-checked + .MuiSwitch-track": { backgroundColor: T.primary } }}
          />
        </Box>
        <Typography sx={{ fontSize: 12, color: T.muted, fontFamily: T.fontBody, mt: "4px" }}>
          Reuse your Link Vault's colours here so both pages match.
        </Typography>
      </Box>

      <GalleryPaletteLibrary open={libOpen} onClose={() => setLibOpen(false)} onSelect={onPaletteSelect} />
    </Box>
  );
};

export default GalleryThemePanel;
